import { ACTIONS, CHALLENGE_STATUS } from './constants';

export const initialState = {
  challenges: {
    instances: {},
    categories: {
      operations: {
        instanceIds: [],
      },
      sales: {
        instanceIds: [],
      },
      marketing: {
        instanceIds: [],
      },
      product: {
        instanceIds: [],
      },
      uncategorized: {
        instanceIds: [],
      }
    }
  }
};

export const reducer = (state, action) => {
  let newState = state;
  
  // Process the action
  switch (action.type) {
    case ACTIONS.SET_CHALLENGES:
      newState = {
        ...state,
        challenges: action.payload
      };
      break;

    case ACTIONS.ATTACH_CATEGORY: {
      const { category_id, instance_id, instance: updatedInstance } = action.payload;
      
      // Validate category exists
      if (!state.challenges.categories[category_id]) {
        return state;
      }

      // Get the current instance if it exists
      const currentInstance = state.challenges.instances[instance_id];
      
      // If instance exists, remove from old category
      if (currentInstance) {
        const oldCategoryId = currentInstance.category_id || 'uncategorized';
        const oldCategory = oldCategoryId === 'uncategorized' 
          ? state.challenges.uncategorized 
          : state.challenges.categories[oldCategoryId];
        
        if (oldCategory && oldCategory.instanceIds) {
          oldCategory.instanceIds = oldCategory.instanceIds.filter(id => id !== instance_id);
        }
      }

      // Add to new category's instanceIds
      const newCategory = state.challenges.categories[category_id];
      if (!newCategory.instanceIds) {
        newCategory.instanceIds = [];
      }
      if (!newCategory.instanceIds.includes(instance_id)) {
        newCategory.instanceIds.push(instance_id);
      }

      // Update state with the instance from API
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          instances: {
            ...state.challenges.instances,
            [instance_id]: updatedInstance || {
              ...(currentInstance || {}),
              id: instance_id,
              category_id
            }
          }
        }
      };
      break;
    }

    case ACTIONS.DETACH_CATEGORY: {
      const { instanceId } = action.payload;
      
      // Get the instance
      const instance = state.challenges.instances[instanceId];
      if (!instance) {
        return state;
      }

      // Remove from current category's instanceIds
      const oldCategoryId = instance.categoryId;
      if (oldCategoryId && oldCategoryId !== 'uncategorized') {
        state.challenges.categories[oldCategoryId].instanceIds.delete(instanceId);
      }

      // Add to uncategorized instanceIds
      state.challenges.uncategorized.instanceIds.add(instanceId);

      // Update instance's categoryId
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          instances: {
            ...state.challenges.instances,
            [instanceId]: {
              ...instance,
              categoryId: 'uncategorized'
            }
          }
        }
      };
      break;
    }

    case ACTIONS.ADD_CHALLENGE_INSTANCE: {
      const { instance, category_id } = action.payload;
      const categoryId = category_id || 'uncategorized';
      
      return {
        ...state,
        challenges: {
          ...state.challenges,
          instances: {
            ...state.challenges.instances,
            [instance.id]: instance
          },
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              instanceIds: [...state.challenges.categories[categoryId].instanceIds, instance.id]
            }
          }
        }
      };
    }

    case ACTIONS.UPDATE_CHALLENGE_INSTANCE: {
      const { instance, category_id } = action.payload;
      const oldInstance = state.challenges.instances[instance.id];
      const oldCategoryId = oldInstance?.category_id || 'uncategorized';
      const newCategoryId = category_id || oldCategoryId;
      
      // Create new state with updated categories
      const newCategories = { ...state.challenges.categories };
      
      // Remove from old category if it changed
      if (oldCategoryId !== newCategoryId) {
        newCategories[oldCategoryId] = {
          ...newCategories[oldCategoryId],
          instanceIds: newCategories[oldCategoryId].instanceIds.filter(id => id !== instance.id)
        };
        
        // Add to new category
        newCategories[newCategoryId] = {
          ...newCategories[newCategoryId],
          instanceIds: [...newCategories[newCategoryId].instanceIds, instance.id]
        };
      }
      
      // Update state
      return {
        ...state,
        challenges: {
          ...state.challenges,
          instances: {
            ...state.challenges.instances,
            [instance.id]: instance
          },
          categories: newCategories
        }
      };
    }

    case ACTIONS.ARCHIVE_CHALLENGE_INSTANCE: {
      const { categoryId, instanceId } = action.payload;
      
      // Get the instance
      const instance = state.challenges.instances[instanceId];
      if (!instance) {
        return state;
      }

      // Toggle isArchived status
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          instances: {
            ...state.challenges.instances,
            [instanceId]: {
              ...instance,
              isArchived: !instance.isArchived
            }
          }
        }
      };
      break;
    }

    case ACTIONS.DELETE_CHALLENGE_INSTANCE: {
      const { instanceId } = action.payload;
      
      // Get the instance to find its category
      const instance = state.challenges.instances[instanceId];
      if (!instance) {
        return state;
      }

      // Remove from category's instanceIds
      const categoryId = instance.categoryId || 'uncategorized';
      if (categoryId === 'uncategorized') {
        state.challenges.uncategorized.instanceIds.delete(instanceId);
      } else {
        state.challenges.categories[categoryId].instanceIds.delete(instanceId);
      }

      // Remove from instances
      const { [instanceId]: removed, ...remainingInstances } = state.challenges.instances;
      
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          instances: remainingInstances
        }
      };
      break;
    }

    case ACTIONS.ADD_CHALLENGE_DRAFT: {
      const { categoryId, draft } = action.payload;
      const category = state.challenges.categories[categoryId];
      
      if (!category) {
        return state;
      }

      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...category,
              challengeDrafts: {
                ...category.challengeDrafts,
                [draft.id]: draft
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.ATTACH_CHALLENGE: {
      const { category_id, challenge } = action.payload;
      
      // Validate category exists
      if (!state.challenges.categories[category_id]) {
        return state;
      }

      // Get the instance
      const instance = state.challenges.instances[challenge.instance_id];
      if (!instance) {
        return state;
      }

      // Update instance status to ACTIVE
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          instances: {
            ...state.challenges.instances,
            [challenge.instance_id]: {
              ...instance,
              status: CHALLENGE_STATUS.ACTIVE,
              name: challenge.name
            }
          }
        }
      };
      break;
    }

    case ACTIONS.DETACH_CHALLENGE: {
      const { categoryId, challengeId } = action.payload;
      
      // Find instance with this id
      const instance = state.challenges.instances[challengeId];

      if (!instance) {
        return state;
      }

      // Set instance back to DRAFT status
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          instances: {
            ...state.challenges.instances,
            [challengeId]: {
              ...instance,
              status: CHALLENGE_STATUS.DRAFT
            }
          }
        }
      };
      break;
    }

    case ACTIONS.DELETE_CHALLENGE_DRAFT: {
      const { categoryId, draftId } = action.payload;
      
      // Validate category exists
      if (!state.challenges.categories[categoryId]) {
        return state;
      }

      // Check if draft is attached to any instance
      const attachedInstance = Object.values(state.challenges.instances)
        .find(inst => inst.draftId === draftId);

      if (attachedInstance) {
        return state;
      }

      // Remove draft from category's challengeDrafts
      const { [draftId]: removedDraft, ...remainingDrafts } = 
        state.challenges.categories[categoryId].challengeDrafts;

      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: remainingDrafts
            }
          }
        }
      };
      break;
    }

    case ACTIONS.ADD_DRAFT_OPPORTUNITY: {
      const { categoryId, draftId, opportunity } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  opportunities: [...(draft.opportunities || []), opportunity]
                }
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.ADD_DRAFT_RISK: {
      const { categoryId, draftId, risk } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  risks: [...(draft.risks || []), risk]
                }
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.UPDATE_DRAFT_OPPORTUNITY: {
      const { categoryId, draftId, opportunityId, content } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  opportunities: draft.opportunities.map(opp => 
                    opp.id === opportunityId ? { ...opp, content } : opp
                  )
                }
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.UPDATE_DRAFT_RISK: {
      const { categoryId, draftId, riskId, content } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  risks: draft.risks.map(risk => 
                    risk.id === riskId ? { ...risk, content } : risk
                  )
                }
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.DELETE_DRAFT_OPPORTUNITY: {
      const { categoryId, draftId, opportunityId } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  opportunities: draft.opportunities.filter(opp => opp.id !== opportunityId)
                }
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.DELETE_DRAFT_RISK: {
      const { categoryId, draftId, riskId } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  risks: draft.risks.filter(risk => risk.id !== riskId)
                }
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.ACTIVATE_DRAFT_OPPORTUNITY: {
      const { categoryId, draftId, opportunityId } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      
      // Deactivate all opportunities and activate the selected one
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  opportunities: draft.opportunities.map(opp => ({
                    ...opp,
                    isActive: opp.id === opportunityId
                  }))
                }
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.ACTIVATE_DRAFT_RISK: {
      const { categoryId, draftId, riskId } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      
      // Deactivate all risks and activate the selected one
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  risks: draft.risks.map(risk => ({
                    ...risk,
                    isActive: risk.id === riskId
                  }))
                }
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.ADD_DRAFT_ACTION: {
      const { categoryId, draftId, itemType, itemId, action: actionData } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      const items = itemType === 'opportunities' ? draft.opportunities : draft.risks;
      const item = items.find(i => i.id === itemId);
      
      if (!item) {
        return state;
      }

      // Add the action to the item's actions array
      const updatedItem = {
        ...item,
        actions: [...(item.actions || []), actionData]
      };

      // Update the item in the state
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  [itemType]: items.map(i => i.id === itemId ? updatedItem : i)
                }
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.UPDATE_DRAFT_ACTION: {
      const { categoryId, draftId, itemType, itemId, actionId, updates } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      const items = itemType === 'opportunities' ? draft.opportunities : draft.risks;
      const item = items.find(i => i.id === itemId);
      
      if (!item) {
        return state;
      }

      // Update the action in the item
      const updatedItems = items.map(i => {
        if (i.id === itemId) {
          return {
            ...i,
            actions: i.actions.map(a => 
              a.id === actionId ? { ...a, ...updates } : a
            )
          };
        }
        return i;
      });

      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  [itemType]: updatedItems
                }
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.DELETE_DRAFT_ACTION: {
      const { categoryId, draftId, itemType, itemId, actionId } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      const items = itemType === 'opportunities' ? draft.opportunities : draft.risks;
      const item = items.find(i => i.id === itemId);
      
      if (!item) {
        return state;
      }

      // Remove the action from the item
      const updatedItems = items.map(i => {
        if (i.id === itemId) {
          return {
            ...i,
            actions: i.actions.filter(a => a.id !== actionId)
          };
        }
        return i;
      });

      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  [itemType]: updatedItems
                }
              }
            }
          }
        }
      };
      break;
    }

    case ACTIONS.SET_ACTIVE_ITEM_TYPE: {
      const { categoryId, draftId, itemType } = action.payload;
      
      // Validate category and draft exist
      if (!state.challenges.categories[categoryId]?.challengeDrafts[draftId]) {
        return state;
      }

      const draft = state.challenges.categories[categoryId].challengeDrafts[draftId];
      
      newState = {
        ...state,
        challenges: {
          ...state.challenges,
          categories: {
            ...state.challenges.categories,
            [categoryId]: {
              ...state.challenges.categories[categoryId],
              challengeDrafts: {
                ...state.challenges.categories[categoryId].challengeDrafts,
                [draftId]: {
                  ...draft,
                  activeItemType: itemType
                }
              }
            }
          }
        }
      };
      break;
    }

    default:
      return state;
  }
  return newState;
};
